import { useState } from 'react'
import { createPortal } from 'react-dom'

// Lets an examiner scope generation to specific chapters of a textbook by typing
// the PRINTED page numbers themselves — not the PDF's raw page position, since
// those differ whenever a preface/table of contents precedes the book's own
// numbering. The examiner needs to read the number directly off the actual page
// in their own copy of the PDF before entering it here.
export default function PageRangePicker({ fileName, initialRanges, onDone, onClose }) {
  const [ranges, setRanges] = useState(initialRanges?.length ? initialRanges : [[1, 1]])

  function updateRange(i, key, value) {
    // Clamp to >= 1 — page numbers can't be zero or negative, and the min="1"
    // input attribute alone doesn't stop someone from typing 0 or "-5".
    const n = Math.max(1, parseInt(value, 10) || 1)
    setRanges(prev => prev.map((r, idx) => idx !== i ? r : (key === 'start' ? [n, r[1]] : [r[0], n])))
  }

  function addRange() {
    setRanges(prev => [...prev, [1, 1]])
  }

  function removeRange(i) {
    setRanges(prev => prev.filter((_, idx) => idx !== i))
  }

  function handleDone() {
    const clean = ranges
      .filter(([s, e]) => Number.isInteger(s) && Number.isInteger(e) && s >= 1 && e >= 1)
      .map(([s, e]) => [Math.min(s, e), Math.max(s, e)])
    onDone(clean)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
         onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden"
           style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
           onClick={e => e.stopPropagation()}>

        <div className="px-6 py-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-base font-bold" style={{ color: 'var(--text-1)' }}>Configure Page Range</h2>
          <p className="text-xs mt-1 font-medium break-words" style={{ color: 'var(--text-2)' }}>{fileName}</p>
          <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--text-3)' }}>
            Enter the page number <b>printed on the page itself</b>, not its position in the file —
            these can differ if a preface or table of contents comes before the book's own numbering starts.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-80 overflow-y-auto">
          {ranges.map(([start, end], i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>From</span>
              <input type="number" min="1" value={start} onChange={e => updateRange(i, 'start', e.target.value)}
                className="w-20 text-center text-sm font-bold rounded-lg py-1.5"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>to</span>
              <input type="number" min="1" value={end} onChange={e => updateRange(i, 'end', e.target.value)}
                className="w-20 text-center text-sm font-bold rounded-lg py-1.5"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
              {ranges.length > 1 && (
                <button type="button" onClick={() => removeRange(i)} className="ml-auto text-sm flex-shrink-0" style={{ color: 'var(--red)' }}>✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addRange} className="text-xs font-medium" style={{ color: 'var(--brand)' }}>+ Add another range</button>
        </div>

        <div className="px-6 py-4 flex justify-end gap-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={handleDone} className="btn-primary">Done</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
